import { Crown, TrendingDown, Trophy } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../../lib/utils";
import { DriverPerformance } from "../@types";
import { DashCard, EmptyState } from "./DashCard";
import DriverAvatar from "./DriverAvatar";

const MAX_RANKED = 8;

const rankBadge = (rank: number) => {
  if (rank === 1) return "bg-amber-100 text-amber-700";
  if (rank === 2) return "bg-slate-200 text-slate-700";
  if (rank === 3) return "bg-orange-100 text-orange-700";
  return "bg-slate-100 text-slate-500";
};

interface SpotlightProps {
  label: string;
  driver: DriverPerformance;
  variant: "best" | "worst";
  onClick: () => void;
}

const Spotlight: React.FC<SpotlightProps> = ({
  label,
  driver,
  variant,
  onClick,
}) => {
  const isBest = variant === "best";
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full min-w-0 flex-col items-start gap-3 rounded-xl border p-3.5 text-left transition-colors",
        isBest
          ? "border-emerald-100 bg-emerald-50/60 hover:border-emerald-200"
          : "border-red-100 bg-red-50/50 hover:border-red-200"
      )}
    >
      <span
        className={cn(
          "inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide",
          isBest ? "text-emerald-700" : "text-red-600"
        )}
      >
        {isBest ? <Crown size={13} /> : <TrendingDown size={13} />}
        {label}
      </span>
      <div className="flex w-full min-w-0 items-center gap-2.5">
        <DriverAvatar
          name={driver.driverName}
          imageUrl={driver.imageUrl}
          className="h-10 w-10"
        />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">
            {driver.driverName}
          </p>
          <p className="text-xs text-slate-500">
            {driver.totalTrips.toLocaleString()} trips · #{driver.rank}
          </p>
        </div>
      </div>
    </button>
  );
};

interface DriverPerformanceCardProps {
  best: DriverPerformance | null;
  worst: DriverPerformance | null;
  ranking: DriverPerformance[];
  className?: string;
}

const DriverPerformanceCard: React.FC<DriverPerformanceCardProps> = ({
  best,
  worst,
  ranking,
  className,
}) => {
  const navigate = useNavigate();
  const goToDriver = (id: string) => navigate(`/admin/drivers/${id}`);

  const sorted = [...ranking].sort((a, b) => a.rank - b.rank);
  const visible = sorted.slice(0, MAX_RANKED);
  const topTrips = Math.max(1, ...sorted.map((d) => d.totalTrips));
  // Only show the worst performer separately when it differs from the best.
  const showWorst = worst && worst.driverId !== best?.driverId;

  return (
    <DashCard
      title="Driver performance"
      subtitle="Ranked by trips completed"
      className={className}
      bodyClassName="pt-4"
      action={
        sorted.length > 0 && (
          <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
            {sorted.length} ranked
          </span>
        )
      }
    >
      {!best && sorted.length === 0 ? (
        <EmptyState
          icon={<Trophy size={20} />}
          message="No driver performance data yet"
        />
      ) : (
        <div className="space-y-5">
          {best && (
            <div
              className={cn(
                "grid gap-3",
                showWorst ? "grid-cols-1 xs:grid-cols-2" : "grid-cols-1"
              )}
            >
              <Spotlight
                label="Top performer"
                driver={best}
                variant="best"
                onClick={() => goToDriver(best.driverId)}
              />
              {showWorst && (
                <Spotlight
                  label="Needs support"
                  driver={worst}
                  variant="worst"
                  onClick={() => goToDriver(worst.driverId)}
                />
              )}
            </div>
          )}

          {visible.length > 0 && (
            <ol className="space-y-1">
              {visible.map((driver) => (
                <li key={driver.driverId}>
                  <button
                    type="button"
                    onClick={() => goToDriver(driver.driverId)}
                    className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-slate-50"
                  >
                    <span
                      className={cn(
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
                        rankBadge(driver.rank)
                      )}
                    >
                      {driver.rank}
                    </span>
                    <DriverAvatar
                      name={driver.driverName}
                      imageUrl={driver.imageUrl}
                      className="h-8 w-8"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-medium text-slate-800">
                          {driver.driverName}
                        </p>
                        <span className="shrink-0 text-xs font-semibold text-slate-900">
                          {driver.totalTrips.toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{
                            width: `${(driver.totalTrips / topTrips) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ol>
          )}

          {sorted.length > MAX_RANKED && (
            <button
              type="button"
              onClick={() => navigate("/admin/drivers")}
              className="w-full rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              View all {sorted.length} drivers
            </button>
          )}
        </div>
      )}
    </DashCard>
  );
};

export default DriverPerformanceCard;
